import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, CircleSlash } from 'lucide-react'
import { ApiError } from '@/lib/api-client'
import { formatDateTimeCompact } from '@/lib/datetime'
import { decimalInputToMinor, formatMinorCurrency, minorToDecimalInput } from '@/lib/format'
import { queryKeys } from '@/lib/query-client'
import type { CancellationOptions, PenaltyMode, RefundMode } from '@/types/api'
import { useAdminMutation } from '@/hooks/useAdminMutation'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { KeyValue, KeyValueGrid } from '@/components/ui/RowActions'
import { Spinner } from '@/components/ui/Spinner'
import { cancelTripRequest, fetchCancellationOptions } from './api'
import styles from './CancelBookingDialog.module.css'

interface CancelBookingDialogProps {
  /**
   * The booking to cancel, by its trip-request id. Null closes the dialog.
   *
   * Deliberately an id rather than a row: the trips screen opens the same dialog
   * from `trip.requestId`, and everything shown inside — customer, reference,
   * policy band, refundable balance — is read from the server anyway. Matching
   * `RefundDialog`, which is opened from both screens for the same reason.
   */
  tripRequestId: number | null
  onClose: () => void
}

/**
 * The operator's cancellation dialog.
 *
 * Its shape comes from the server, not from a payment-status string: a booking
 * with captured card money asks how much to send back, and one without asks
 * whether to charge a cancellation penalty. A booking marked CARD whose payment
 * never landed is the second kind, and no amount of reasoning in the browser
 * would work that out reliably.
 *
 * The amount is defaulted to what the cancellation policy would have produced
 * for this booking at this moment and labelled as exactly that, so the common
 * case is one click while the operator stays the one deciding.
 *
 * It is the same dialog whether the booking is still pending or has a driver on
 * it. A confirmed booking is cancelled by exactly the same call — the trips
 * screen simply had no button for it, so the office had to ring the driver and
 * leave the booking standing.
 */
export function CancelBookingDialog({ tripRequestId, onClose }: CancelBookingDialogProps) {
  const open = tripRequestId !== null
  const requestId = tripRequestId

  const [refundMode, setRefundMode] = useState<RefundMode>('FULL')
  const [penaltyMode, setPenaltyMode] = useState<PenaltyMode>('APPLY')
  const [amountInput, setAmountInput] = useState('')
  const [reason, setReason] = useState('')

  const optionsQuery = useQuery({
    queryKey: queryKeys.tripRequests.cancellationOptions(requestId ?? 0),
    queryFn: () => fetchCancellationOptions(requestId as number),
    enabled: open,
    // Always re-read on open: the policy band depends on the clock, and a row
    // rendered ten minutes ago may have crossed a boundary since.
    staleTime: 0,
    gcTime: 0,
  })

  const options = optionsQuery.data
  const isRefund = options?.mode === 'REFUND'

  // Default to the policy figure once the server has said what it is.
  useEffect(() => {
    if (!options) return
    setRefundMode('FULL')
    setPenaltyMode(options.policyPenaltyMinor > 0 ? 'APPLY' : 'NONE')
    setAmountInput(
      minorToDecimalInput(options.mode === 'REFUND' ? options.policyRefundMinor : options.policyPenaltyMinor),
    )
    setReason('')
  }, [options])

  const amountMinor = useMemo(() => decimalInputToMinor(amountInput), [amountInput])

  const amountRequired =
    (isRefund && refundMode === 'PARTIAL') || (!isRefund && penaltyMode === 'APPLY')

  const amountError = useMemo(() => {
    if (!options || !amountRequired) return undefined
    if (amountInput.trim() === '') return 'Enter an amount.'
    if (amountMinor === null) return 'Enter a valid amount, for example 212.50.'
    if (amountMinor <= 0) return 'The amount must be greater than zero.'
    if (isRefund && amountMinor > options.remainingRefundableMinor) {
      return `That is more than the ${formatMinorCurrency(
        options.remainingRefundableMinor,
        options.currency,
      )} still refundable.`
    }
    return undefined
  }, [amountInput, amountMinor, amountRequired, isRefund, options])

  const reasonError =
    reason.trim().length > 0 && reason.trim().length < 3
      ? 'Give at least a few words — this is recorded against the cancellation.'
      : undefined

  const mutation = useAdminMutation({
    mutationFn: () =>
      cancelTripRequest(requestId as number, {
        refundMode: isRefund ? refundMode : 'NONE',
        refundAmountMinor:
          isRefund && refundMode === 'PARTIAL' ? (amountMinor as number) : undefined,
        penaltyMode: isRefund ? 'NONE' : penaltyMode,
        penaltyAmountMinor:
          !isRefund && penaltyMode === 'APPLY' ? (amountMinor as number) : undefined,
        reason: reason.trim(),
      }),
    successTitle: 'Booking cancelled',
    errorTitle: 'The booking was not cancelled',
    invalidate: [
      queryKeys.tripRequests.all,
      queryKeys.trips.all,
      queryKeys.refunds.all,
      queryKeys.penalties.all,
      queryKeys.dashboard.all,
    ],
    onSuccess: () => onClose(),
  })

  const canSubmit =
    Boolean(options?.cancellable) &&
    !amountError &&
    reason.trim().length >= 3 &&
    !mutation.isPending

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Cancel this booking"
      size="md"
      busy={mutation.isPending}
      description="The customer's booking will be cancelled. What happens to the money is your decision."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Keep booking
          </Button>
          <Button
            variant="danger"
            iconLeft={<CircleSlash aria-hidden="true" />}
            loading={mutation.isPending}
            disabled={!canSubmit}
            onClick={() => mutation.mutate(undefined)}
          >
            {mutation.isPending ? 'Cancelling…' : 'Cancel booking'}
          </Button>
        </>
      }
    >
      {optionsQuery.isLoading ? (
        <div className={styles.loading}>
          <Spinner />
          <p>Checking this booking&rsquo;s cancellation terms…</p>
        </div>
      ) : optionsQuery.isError ? (
        <div className={styles.blocked} role="alert">
          <AlertTriangle aria-hidden="true" />
          <div>
            <p className={styles.blockedTitle}>Could not check this booking</p>
            <p>
              {optionsQuery.error instanceof ApiError
                ? optionsQuery.error.message
                : 'The cancellation check failed.'}
            </p>
            <Button variant="secondary" size="sm" onClick={() => void optionsQuery.refetch()}>
              Try again
            </Button>
          </div>
        </div>
      ) : options ? (
        <div className={styles.body}>
          <Summary options={options} />

          {!options.cancellable ? (
            <div className={styles.blocked} role="alert">
              <AlertTriangle aria-hidden="true" />
              <div>
                <p className={styles.blockedTitle}>This booking cannot be cancelled</p>
                <p>{options.blockedReason}</p>
              </div>
            </div>
          ) : (
            <>
              {isRefund ? (
                <RefundChoice
                  options={options}
                  mode={refundMode}
                  onModeChange={setRefundMode}
                  disabled={mutation.isPending}
                />
              ) : (
                <PenaltyChoice
                  options={options}
                  mode={penaltyMode}
                  onModeChange={setPenaltyMode}
                  disabled={mutation.isPending}
                />
              )}

              {amountRequired ? (
                <Field
                  label={isRefund ? 'Refund amount' : 'Penalty amount'}
                  required
                  error={amountError}
                  hint={
                    isRefund
                      ? `Up to ${formatMinorCurrency(options.remainingRefundableMinor, options.currency)}.`
                      : 'Added to the customer&rsquo;s next booking.'
                  }
                >
                  {({ id, describedBy, invalid }) => (
                    <Input
                      id={id}
                      aria-describedby={describedBy}
                      invalid={invalid}
                      inputMode="decimal"
                      value={amountInput}
                      disabled={mutation.isPending}
                      onChange={(event) => setAmountInput(event.target.value)}
                      addonRight={<span className={styles.currency}>{options.currency}</span>}
                    />
                  )}
                </Field>
              ) : null}

              <Field
                label="Reason"
                required
                error={reasonError}
                hint="Recorded permanently against the cancellation, the refund and any penalty."
              >
                {({ id, describedBy, invalid }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    invalid={invalid}
                    value={reason}
                    maxLength={500}
                    disabled={mutation.isPending}
                    placeholder="Customer called to cancel, no driver available…"
                    onChange={(event) => setReason(event.target.value)}
                  />
                )}
              </Field>

              {isRefund && refundMode !== 'NONE' ? (
                <div className={styles.warning} role="note">
                  <AlertTriangle aria-hidden="true" />
                  <p>
                    Confirming sends a real refund to Paymob. The money leaves the merchant account immediately
                    and reaches the customer&rsquo;s card in a few business days.
                  </p>
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </Dialog>
  )
}

/** What the policy makes of this booking, in the server's own numbers. */
function Summary({ options }: { options: CancellationOptions }) {
  const band =
    options.policyBand === 'FULL'
      ? 'Full refund'
      : options.policyBand === 'HALF'
        ? '50% refund'
        : 'No refund'

  return (
    <section className={styles.summary}>
      <KeyValueGrid>
        <KeyValue label="Customer">
          {options.customerName ?? '—'}
          {options.customerPhone ? <div className={styles.muted}>{options.customerPhone}</div> : null}
        </KeyValue>
        <KeyValue label="Booking">
          {options.bookingReference ?? `#${options.tripRequestId}`}
        </KeyValue>
        <KeyValue label="Service">
          {options.serviceType === 'TRAVEL' ? 'Out-of-Cairo' : 'Cairo'}
        </KeyValue>
        <KeyValue label="Payment">{options.paymentMethod ?? '—'}</KeyValue>
        <KeyValue label="Scheduled pickup">
          {options.scheduleKnown ? formatDateTimeCompact(options.scheduledPickupAt) : 'Not recorded'}
        </KeyValue>
        <KeyValue label="Policy band">
          <strong className={styles.band}>{band}</strong>
          <div className={styles.muted}>{describeWindow(options)}</div>
        </KeyValue>
      </KeyValueGrid>

      {options.settledPenaltyAmountMinor > 0 ? (
        <p className={styles.note}>
          {formatMinorCurrency(options.settledPenaltyAmountMinor, options.currency)} of this charge settled an
          earlier cancellation penalty. The policy percentage applies to the{' '}
          {formatMinorCurrency(options.tripAmountMinor, options.currency)} fare only.
        </p>
      ) : null}
    </section>
  )
}

/**
 * How the band was arrived at, in words.
 *
 * An operator who can see "cancelled 14h before pickup" next to "50% refund"
 * can answer a customer's question without opening the policy document.
 */
function describeWindow(options: CancellationOptions): string {
  if (!options.scheduleKnown) {
    return 'This booking has no recorded pickup time, so it defaults to a full refund.'
  }
  const minutes = options.minutesUntilPickup ?? 0
  if (minutes < 0) {
    return `Cancelled ${formatDuration(-minutes)} after the scheduled pickup.`
  }
  return `Cancelled ${formatDuration(minutes)} before pickup.`
}

function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (hours === 0) return `${rest} min`
  if (rest === 0) return `${hours}h`
  return `${hours}h ${rest}m`
}

function RefundChoice({
  options,
  mode,
  onModeChange,
  disabled,
}: {
  options: CancellationOptions
  mode: RefundMode
  onModeChange: (mode: RefundMode) => void
  disabled: boolean
}) {
  return (
    <fieldset className={styles.choices} disabled={disabled}>
      <legend className={styles.legend}>What should happen to the payment?</legend>

      <Choice
        name="refundMode"
        value="FULL"
        checked={mode === 'FULL'}
        onChange={() => onModeChange('FULL')}
        label={`Refund the full remaining balance — ${formatMinorCurrency(
          options.remainingRefundableMinor,
          options.currency,
        )}`}
        hint={
          options.policyRefundMinor === options.remainingRefundableMinor
            ? 'This is also what the policy allows for this booking.'
            : undefined
        }
      />
      <Choice
        name="refundMode"
        value="PARTIAL"
        checked={mode === 'PARTIAL'}
        onChange={() => onModeChange('PARTIAL')}
        label="Refund a specific amount"
        hint={`The policy figure for this booking is ${formatMinorCurrency(
          options.policyRefundMinor,
          options.currency,
        )}.`}
        disabled={!options.partialRefundSupported}
      />
      <Choice
        name="refundMode"
        value="NONE"
        checked={mode === 'NONE'}
        onChange={() => onModeChange('NONE')}
        label="No refund"
        hint="Nothing is sent to Paymob."
      />
    </fieldset>
  )
}

function PenaltyChoice({
  options,
  mode,
  onModeChange,
  disabled,
}: {
  options: CancellationOptions
  mode: PenaltyMode
  onModeChange: (mode: PenaltyMode) => void
  disabled: boolean
}) {
  return (
    <fieldset className={styles.choices} disabled={disabled}>
      <legend className={styles.legend}>
        There is no captured payment to refund. Should the customer be charged a cancellation penalty?
      </legend>

      <Choice
        name="penaltyMode"
        value="APPLY"
        checked={mode === 'APPLY'}
        onChange={() => onModeChange('APPLY')}
        label="Charge a penalty"
        hint={`The policy figure for this booking is ${formatMinorCurrency(
          options.policyPenaltyMinor,
          options.currency,
        )}. It is added to the customer's next booking and cannot be paid in cash.`}
      />
      <Choice
        name="penaltyMode"
        value="NONE"
        checked={mode === 'NONE'}
        onChange={() => onModeChange('NONE')}
        label="No penalty"
        hint="The customer owes nothing."
      />
    </fieldset>
  )
}

function Choice({
  name,
  value,
  checked,
  onChange,
  label,
  hint,
  disabled,
}: {
  name: string
  value: string
  checked: boolean
  onChange: () => void
  label: string
  hint?: string
  disabled?: boolean
}) {
  return (
    <label className={styles.choice}>
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      <span>
        <span className={styles.choiceLabel}>{label}</span>
        {hint ? <span className={styles.choiceHint}>{hint}</span> : null}
      </span>
    </label>
  )
}
